import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../api";
import { getAttribution } from "../attribution";
import { PROGRAM_OPTIONS, REFERRAL_OPTIONS } from "../data/siteData";

const inputCls =
  "w-full bg-[#161616] border border-white/15 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brand/60 focus:border-brand transition-colors";
const labelCls = "block text-sm font-semibold text-white/90 mb-1.5";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = Array.from({ length: 51 }, (_, i) => 2026 - i);

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className={labelCls}>
      {label} {required && <span className="text-brand">*</span>}
    </label>
    {children}
  </div>
);

const YesNo = ({ name, value, onChange, options }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${name}-group`}>
    {options.map((o) => (
      <button
        type="button"
        key={o.value}
        data-testid={`${name}-${o.value}`}
        onClick={() => onChange(o.value)}
        className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
          value === o.value ? "bg-brand border-brand text-white" : "border-white/20 text-white/70 hover:border-white/50"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const initial = {
  first_name: "", last_name: "", email: "", phone: "",
  dog_name: "", dog_breed: "", birth_month: "", birth_day: "", birth_year: "",
  behavioral_issues: "", training_goals: "",
  bitten: "no", bitten_details: "",
  medical_issues: "no", medical_details: "",
  additional_info: "", reviewed_programs: "yes", familiar_tools: "yes",
  programs: [], referral_source: "", veteran: "no",
};

export const LeadForm = ({ compact = false, defaultPrograms = [] }) => {
  const [form, setForm] = useState({ ...initial, programs: defaultPrograms });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const location = useLocation();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const toggleProgram = (p) =>
    setForm((f) => ({ ...f, programs: f.programs.includes(p) ? f.programs.filter((x) => x !== p) : [...f.programs, p] }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const dog_birthdate =
        form.birth_month && form.birth_day && form.birth_year
          ? `${form.birth_month} ${form.birth_day}, ${form.birth_year}`
          : "";
      const { birth_month, birth_day, birth_year, ...rest } = form;
      await api.post("/leads", { ...rest, dog_birthdate, source_page: location.pathname, ...getAttribution() });
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { event_category: "lead", event_label: location.pathname });
      }
      setDone(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#121212] border border-brand/40 rounded-2xl p-10 text-center" data-testid="lead-form-success">
        <div className="w-14 h-14 rounded-full bg-brand/15 text-brand flex items-center justify-center mx-auto mb-5 text-2xl font-heading font-bold">✓</div>
        <h3 className="font-heading text-2xl font-bold text-white mb-3">Thank you, {form.first_name}!</h3>
        <p className="text-white/70 leading-relaxed">
          Your enquiry has been received. We review every submission personally and will reach out within one business day.
          Need us sooner? Call <a href="tel:+14037150499" className="text-brand font-semibold">403-715-0499</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="lead-form">
      <p className="text-sm text-white/50 leading-relaxed">
        Before filling out our contact form, please review our training programs and FAQ to learn about the tools and methods we use.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="First Name" required testid="field-first-name">
          <input required className={inputCls} data-testid="lead-first-name" value={form.first_name} onChange={set("first_name")} placeholder="Jane" />
        </Field>
        <Field label="Last Name" required testid="field-last-name">
          <input required className={inputCls} data-testid="lead-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Smith" />
        </Field>
        <Field label="Email" required testid="field-email">
          <input required type="email" className={inputCls} data-testid="lead-email" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
        </Field>
        <Field label="Cell Phone Number" required testid="field-phone">
          <input required type="tel" className={inputCls} data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="403-555-0123" />
        </Field>
        <Field label="Dog's Name" testid="field-dog-name">
          <input className={inputCls} data-testid="lead-dog-name" value={form.dog_name} onChange={set("dog_name")} placeholder="Rex" />
        </Field>
        <Field label="Dog Breed (or best guess)" testid="field-dog-breed">
          <input className={inputCls} data-testid="lead-dog-breed" value={form.dog_breed} onChange={set("dog_breed")} placeholder="German Shepherd" />
        </Field>
      </div>

      <Field label="Dog's Birthdate (or best guess)" testid="field-dog-birthdate">
        <div className="grid grid-cols-3 gap-3">
          <select className={inputCls} data-testid="lead-birth-month" value={form.birth_month} onChange={set("birth_month")}>
            <option value="">Month</option>
            {MONTHS.map((m) => <option key={m}>{m}</option>)}
          </select>
          <select className={inputCls} data-testid="lead-birth-day" value={form.birth_day} onChange={set("birth_day")}>
            <option value="">Day</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d}>{d}</option>)}
          </select>
          <select className={inputCls} data-testid="lead-birth-year" value={form.birth_year} onChange={set("birth_year")}>
            <option value="">Year</option>
            {YEARS.map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>
      </Field>

      <Field label="Describe Behavioural Issues" testid="field-behavioral">
        <textarea rows={3} className={inputCls} data-testid="lead-behavioral-issues" value={form.behavioral_issues} onChange={set("behavioral_issues")} placeholder="Pulling on leash, barking at other dogs..." />
      </Field>
      <Field label="Training Goals" testid="field-goals">
        <textarea rows={3} className={inputCls} data-testid="lead-training-goals" value={form.training_goals} onChange={set("training_goals")} placeholder="Off-leash reliability, calm walks..." />
      </Field>

      <Field label="Has your dog bitten in the past?" testid="field-bitten">
        <YesNo name="lead-bitten" value={form.bitten} onChange={set("bitten")}
          options={[{ value: "no", label: "No" }, { value: "human", label: "Yes (Human)" }, { value: "dog", label: "Yes (Dog)" }, { value: "both", label: "Yes (Both)" }]} />
      </Field>
      {form.bitten !== "no" && (
        <Field label='If you answered "yes" above, briefly describe.' testid="field-bitten-details">
          <textarea rows={2} className={inputCls} data-testid="lead-bitten-details" value={form.bitten_details} onChange={set("bitten_details")} />
        </Field>
      )}

      <Field label="Does your dog have any medical issues or ailments?" testid="field-medical">
        <YesNo name="lead-medical" value={form.medical_issues} onChange={set("medical_issues")}
          options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
      </Field>
      {form.medical_issues === "yes" && (
        <Field label='If you answered "yes" above, please briefly describe.' testid="field-medical-details">
          <textarea rows={2} className={inputCls} data-testid="lead-medical-details" value={form.medical_details} onChange={set("medical_details")} />
        </Field>
      )}

      {!compact && (
        <Field label="Please detail any further information important for us to know." testid="field-additional">
          <textarea rows={2} className={inputCls} data-testid="lead-additional-info" value={form.additional_info} onChange={set("additional_info")} />
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Have you reviewed our programs?" testid="field-reviewed">
          <YesNo name="lead-reviewed" value={form.reviewed_programs} onChange={set("reviewed_programs")}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
        </Field>
        <Field label="Are you familiar with our tools and methods?" testid="field-tools">
          <YesNo name="lead-tools" value={form.familiar_tools} onChange={set("familiar_tools")}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
        </Field>
      </div>

      <Field label="Which program(s) are you interested in?" testid="field-programs">
        <div className="flex flex-wrap gap-2" data-testid="lead-programs-group">
          {PROGRAM_OPTIONS.map((p) => (
            <button type="button" key={p} data-testid={`lead-program-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => toggleProgram(p)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                form.programs.includes(p) ? "bg-brand border-brand text-white" : "border-white/20 text-white/70 hover:border-white/50"
              }`}>
              {p}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="How did you hear about us?" testid="field-referral">
          <select className={inputCls} data-testid="lead-referral" value={form.referral_source} onChange={set("referral_source")}>
            <option value="">Select one</option>
            {REFERRAL_OPTIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Veteran or First Responder?" testid="field-veteran">
          <YesNo name="lead-veteran" value={form.veteran} onChange={set("veteran")}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
        </Field>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="lead-form-submit-btn"
        className="w-full sm:w-auto bg-brand text-white font-heading font-bold text-base px-10 py-4 rounded-full hover:bg-red-700 transition-colors shadow-[0_0_20px_rgba(220,38,38,0.4)] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {sending && <Loader2 size={18} className="animate-spin" />}
        {sending ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
};
